import React, { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Grid,
  Typography,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  TextField,
  Button,
  FormHelperText,
  InputAdornment,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { CampaignModal, CouponModel } from '@models';
import * as yup from 'yup';

type Props = {
  open: boolean;
  handleClose: () => void;
  allCampaigns: CampaignModal[] | undefined;
  onSave: (coupon: CouponModel) => void;
  isCustomerIDEligable: boolean;
};

const schema = yup.object({
  campaignId: yup.string().required('Campaign is required.'),
});

const CreateCouponModal: FC<Props> = ({ open, handleClose, allCampaigns, onSave, isCustomerIDEligable }) => {
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignModal | null>(null);
  const [discountLimit, setDiscountLimit] = useState<number>(0);
  const [couponValueError, setCouponValueError] = useState<{ value: boolean; message: string }>({ value: false, message: '' });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CouponModel>({
    resolver: yupResolver(schema),
    defaultValues: {
      numberOfCoupons: 1,
      usageLimit: 1,
      expiryDays: 30,
    },
  });

  useEffect(() => {
    if (!open) {
      setDiscountLimit(0);
      setCouponValueError({ value: false, message: '' });
    }
  }, [open]);

  const activeCamps = [
    process.env.REACT_APP_T1_DELIVERY_CAMP_ID,
    process.env.REACT_APP_T1_GOODWILL_CAMP_ID,
    process.env.REACT_APP_T1_OOS_GOODWILL_CAMP_ID,
  ];

  /**
   * Only add the ID campaign if isCustomerIDEligable is true
   */
  if (isCustomerIDEligable) {
    activeCamps.push(process.env.REACT_APP_T1_IDVERIFICATION_CAMP_ID);
  }

  const filterCampaigns = allCampaigns?.length ? allCampaigns.filter((campaign) => activeCamps.includes(campaign.id.toString())) : [];

  const onSubmit: SubmitHandler<CouponModel> = async (data) => {
    // if good will camp pass in the attributes value
    if (data.campaignId === process.env.REACT_APP_T1_GOODWILL_CAMP_ID) {
      data.attributes = {
        coupon_name: 'Goodwill Voucher',
        coupon_value: Number(data.discountLimit),
        Coupon_Display: Number(data.discountLimit),
      };
    } else if (data.campaignId === process.env.REACT_APP_T1_OOS_GOODWILL_CAMP_ID) {
      data.attributes = {
        coupon_name: 'Out of Stock Goodwill Voucher',
        coupon_value: Number(data.discountLimit),
        Coupon_Display: Number(data.discountLimit),
      };
    } else if (data.campaignId === process.env.REACT_APP_T1_DELIVERY_CAMP_ID) {
      data.attributes = {
        coupon_name: 'Free Delivery',
      };

      delete data.discountLimit;
    } else if (data.campaignId === process.env.REACT_APP_T1_IDVERIFICATION_CAMP_ID) {
      data.attributes = {
        coupon_name: '20% Off + Free Delivery',
      };
    }

    if (!discountLimit && selectedCampaign?.id === 98) {
      setCouponValueError({ value: true, message: 'Please enter a dollar value' });
    } else {
      onSave(data);
    }
  };

  const SubmitErrorHandler = (error: any) => {
    console.log(error);
  };

  const renderGoodwillVoucherForm = () => {
    if (
      selectedCampaign?.id === Number(process.env.REACT_APP_T1_GOODWILL_CAMP_ID) ||
      selectedCampaign?.id === Number(process.env.REACT_APP_T1_OOS_GOODWILL_CAMP_ID)
    ) {
      return (
        <>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Controller
                control={control}
                name="discountLimit"
                render={({ field }) => (
                  <TextField
                    data-testid="select-discount-limit"
                    id="filled-basic"
                    variant="filled"
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    type="number"
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '^[0-9](.[0-9]{1,2})?$',
                      min: 0.01,
                      max: 150,
                      step: 0.01,
                    }}
                    label="Dollar value"
                    value={discountLimit || ''}
                    defaultValue={filterCampaigns?.length ? filterCampaigns[0].id : undefined}
                    onChange={(evt) => {
                      setDiscountLimit(evt.target.value as unknown as number);
                      setValue('discountLimit', Number(evt.target.value));
                      setCouponValueError({ value: false, message: '' });
                    }}
                    error={couponValueError.value}
                    helperText={couponValueError.message}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption">
              Please advise the customer that all goodwill vouchers created have an expiry of 30 days from creation
            </Typography>
          </Grid>
        </>
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} data-testid="create-coupons-modal">
      <DialogTitle>
        <Typography variant="h5">Create Coupons</Typography>
      </DialogTitle>
      <DialogContent
        style={{
          paddingTop: 10,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit, SubmitErrorHandler)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Campaign</InputLabel>
                <Controller
                  control={control}
                  name="campaignId"
                  render={({ field }) => (
                    <Select
                      role="listbox"
                      data-testid="select-campaign-dropdown"
                      value={selectedCampaign?.id || ''}
                      {...register('campaignId')}
                      label="Select Campaign"
                      defaultValue={filterCampaigns?.length ? filterCampaigns[0].id : undefined}
                      onChange={(evt) => {
                        const campaign = filterCampaigns?.find((c) => c.id === evt.target.value);

                        setSelectedCampaign(campaign ? campaign : null);
                      }}
                    >
                      {filterCampaigns?.map((campaign) => (
                        <MenuItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.campaignId && <FormHelperText style={{ color: 'red' }}>{errors.campaignId.message}</FormHelperText>}
              </FormControl>
            </Grid>
            {renderGoodwillVoucherForm()}
            <Grid item xs={12}>
              <Box marginTop={2} display="flex" justifyContent="flex-end">
                <Button
                  type="button"
                  data-testid="coupon-form-cancel-button"
                  variant="outlined"
                  onClick={handleClose}
                  sx={{ marginRight: 2 }}
                >
                  Cancel
                </Button>

                <LoadingButton variant="contained" type="submit" data-testid="submit-button">
                  Save
                </LoadingButton>
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCouponModal;
